/* calculator.js
 * Hooks into: [data-box-calculator] regions rendered by snippets/box-calculator.liquid.
 *   Inputs:  [data-length] [data-width] [data-coverage-input]  (numbers, feet / sq ft)
 *   Chips:   [data-waste] buttons (5 / 10 / 15 / 20)
 *   Output:  [data-out-area] [data-out-waste] [data-out-boxes] [data-out-covered]
 *   Product mode: [data-calc-add] sets the product form quantity and submits it.
 * Pure math lives in calcBoxes(): area = length * width; withWaste = area * (1 + waste/100);
 * boxes = ceil(withWaste / coverage - 1e-9); covered = boxes * coverage.
 * No globals: each region is self-managed. Loaded with defer; Export is guarded for tests.
 */
(function () {
  'use strict';

  function toNum(value) {
    if (value === '' || value === null || value === undefined) return NaN;
    var n = Number(value);
    return isFinite(n) ? n : NaN;
  }

  function calcBoxes(length, width, waste, coverage) {
    var l = toNum(length);
    var w = toNum(width);
    var c = toNum(coverage);
    var wr = toNum(waste);
    if (isNaN(wr)) wr = 0;

    if (isNaN(l) || isNaN(w) || isNaN(c) || l <= 0 || w <= 0 || c <= 0) {
      return { area: 0, withWaste: 0, boxes: 0, covered: 0 };
    }

    var area = l * w;
    var withWaste = area * (1 + wr / 100);
    var boxes = Math.ceil(withWaste / c - 1e-9);
    if (boxes < 0) boxes = 0;
    var covered = boxes * c;

    return { area: area, withWaste: withWaste, boxes: boxes, covered: covered };
  }

  function round1(n) {
    return Math.round((n + Number.EPSILON) * 10) / 10;
  }

  function fmt(n) {
    var r = round1(n);
    return (r % 1 === 0) ? String(r) : r.toFixed(1);
  }

  function initRegion(root) {
    var lengthInput = root.querySelector('[data-length]');
    var widthInput = root.querySelector('[data-width]');
    var coverageInput = root.querySelector('[data-coverage-input]');
    var wasteDefault = root.getAttribute('data-waste-default') || '10';
    var coverageFixed = root.getAttribute('data-coverage') || '';
    var waste = wasteDefault;
    var addButton = root.querySelector('[data-calc-add]');
    var productMode = root.getAttribute('data-mode') === 'product';

    var out = {
      area: root.querySelector('[data-out-area]'),
      waste: root.querySelector('[data-out-waste]'),
      boxes: root.querySelector('[data-out-boxes]'),
      covered: root.querySelector('[data-out-covered]'),
      coverage: root.querySelector('[data-out-coverage]')
    };

    var chips = root.querySelectorAll('[data-waste]');

    function readCoverage() {
      if (coverageInput && coverageInput.value !== '') return coverageInput.value;
      return coverageFixed;
    }

    function activeWasteValue() {
      return waste;
    }

    function update() {
      var result = calcBoxes(
        lengthInput ? lengthInput.value : '',
        widthInput ? widthInput.value : '',
        activeWasteValue(),
        readCoverage()
      );

      if (out.area) out.area.textContent = fmt(result.area);
      if (out.waste) out.waste.textContent = fmt(result.withWaste);
      var extra = root.querySelector('[data-out-extra]');
      if (extra) extra.textContent = fmt(Math.max(0, result.covered - result.area));
      var tot = root.querySelector('[data-out-total]');
      var cents = Number(root.getAttribute('data-price-per-box'));
      if (tot && cents > 0) tot.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents * result.boxes / 100);
      var totWrap = root.querySelector('[data-out-total-wrap]');
      if (totWrap) totWrap.hidden = !(cents > 0 && result.boxes > 0);
      if (out.boxes) out.boxes.textContent = String(result.boxes);
      if (out.covered) out.covered.textContent = fmt(result.covered);
      if (out.coverage) out.coverage.textContent = fmt(Number(readCoverage()));

      if (addButton) {
        addButton.disabled = result.boxes <= 0;
        var label = addButton.getAttribute('data-label-template') || 'Add {n} boxes to cart';
        addButton.textContent = label.replace('{n}', String(result.boxes));
      }

      root.setAttribute('data-boxes', String(result.boxes));
      return result;
    }

    function selectChip(chip) {
      waste = chip.getAttribute('data-waste');
      chips.forEach(function (c) {
        var on = c === chip;
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
        c.classList.toggle('is-active', on);
      });
      update();
    }

    if (chips.length) {
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () { selectChip(chip); });
      });
      // mark the default chip active
      var initial = waste;
      var matched = false;
      chips.forEach(function (c) {
        var on = c.getAttribute('data-waste') === String(initial);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
        c.classList.toggle('is-active', on);
        if (on) matched = true;
      });
      if (!matched) waste = chips[0].getAttribute('data-waste');
    }

    [lengthInput, widthInput, coverageInput].forEach(function (el) {
      if (el) el.addEventListener('input', update);
    });

    function fire(el) { el.dispatchEvent(new Event('input', { bubbles: true })); }
    root.querySelectorAll('[data-room]').forEach(function (b) {
      b.addEventListener('click', function () {
        var d = b.getAttribute('data-room').split(',');
        if (lengthInput) { lengthInput.value = d[0]; fire(lengthInput); }
        if (widthInput) { widthInput.value = d[1]; fire(widthInput); }
        root.querySelectorAll('[data-room]').forEach(function (x) { x.classList.toggle('is-active', x === b); });
      });
    });
    root.querySelectorAll('[data-step]').forEach(function (b) {
      b.addEventListener('click', function () {
        var input = b.parentElement.querySelector('input');
        var v = Number(input.value) || 0;
        input.value = String(Math.max(0, Math.round((v + Number(b.getAttribute('data-step')) * 0.5) * 10) / 10));
        fire(input);
      });
    });

    if (addButton) {
      addButton.addEventListener('click', function () {
        if (!productMode) return;
        var result = update();
        if (result.boxes <= 0) return;
        var form = document.querySelector('[data-product-form]');
        if (!form) return;
        var qty = form.querySelector('[name="quantity"]');
        if (qty) qty.value = String(result.boxes);
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
        var live = root.querySelector('[data-calc-status]');
        if (live) live.textContent = result.boxes + ' boxes added to your order.';
      });
    }

    update();
  }

  function init() {
    document.querySelectorAll('[data-box-calculator]').forEach(initRegion);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  if (typeof module !== 'undefined') {
    module.exports = { calcBoxes: calcBoxes };
  }
})();